// src/components/research/ResearchMindmapPdfDialog.jsx
// Dialog zum PDF-Export der Mindmap (ADR 0065). Zwei Modi:
//  - "single": ganze Mindmap auf ein Blatt im gewählten Format skaliert;
//    die Tabelle zeigt für jedes Format Massstab und resultierende Schriftgrösse.
//  - "tiles": Einzelblätter (Kacheln) in frei wählbarem Massstab; die Tabelle
//    zeigt, wie viele Blätter es bei welchem Massstab (quer/hoch) bräuchte.
// Alle Zahlen kommen aus den reinen Layout-Funktionen in utils/mindmapPdf.js,
// dieselben rechnen auch den eigentlichen Export — Vorschau und PDF stimmen
// dadurch überein. Der Snapshot wird beim Öffnen einmal aus dem DOM gelesen.

import React, { useMemo, useState } from "react";
import {
  PAPER_FORMATS,
  TILE_SCALE_PRESETS,
  MAX_TILES,
  PAGE_MARGIN,
  fitSinglePage,
  computeTiles,
  maxPercentForSheets,
  exportMindmapPdf,
  levelsLabel,
} from "../../utils/mindmapPdf";
import ResearchMindmapLevelChips from "./ResearchMindmapLevelChips";

const FORMATS = Object.keys(PAPER_FORMATS).reverse(); // A4 .. A0
const ORIENT_LABEL = { landscape: "quer", portrait: "hoch" };

const fmtPt = (pt) => (pt < 10 ? pt.toFixed(1) : Math.round(pt).toString());

// Lesbarkeit der Fragen-Schrift im PDF.
const readability = (pt) => {
  if (pt < 4) return { cls: "is-bad", text: "kaum lesbar" };
  if (pt < 6.5) return { cls: "is-warn", text: "klein" };
  return { cls: "is-ok", text: "gut lesbar" };
};

// Kleinstes Format, auf dem die Mindmap auf einem Blatt noch lesbar ist.
const defaultSingleFormat = (snap) =>
  FORMATS.find((f) => fitSinglePage(snap, f, "auto").fontPt >= 6) || "A0";

// Schematische Vorschau: Knoten als farbige Rechtecke, darüber Blattraster
// (Kacheln) bzw. die Nutzfläche des einen Blatts.
const Preview = ({ snap, mode, fit, tiles }) => {
  let frames = [];
  let vb = { x: 0, y: 0, w: snap.width, h: snap.height };
  if (mode === "single" && fit) {
    const [aw, ah] = fit.area;
    const fw = aw / fit.mmPerPx;
    const fh = ah / fit.mmPerPx;
    const fx = (snap.width - fw) / 2;
    const fy = (snap.height - fh) / 2;
    frames = [{ x: fx, y: fy, w: fw, h: fh, label: "" }];
    vb = { x: Math.min(0, fx), y: Math.min(0, fy), w: Math.max(snap.width, fw), h: Math.max(snap.height, fh) };
  } else if (mode === "tiles" && tiles && tiles.count <= MAX_TILES) {
    // Tatsächliche Blattausschnitte (linksbündig, Schnitte in Knotenlücken);
    // leere Rasterfelder werden nicht gedruckt und daher nicht gezeigt.
    frames = tiles.pages.map((c) => ({
      x: c.x0,
      y: c.y0,
      w: c.x1 - c.x0,
      h: c.y1 - c.y0,
      label: String(c.nr),
    }));
  }
  const pad = Math.max(vb.w, vb.h) * 0.01;
  const stroke = Math.max(vb.w, vb.h) / 400;
  const fontSize = frames.length ? Math.min(...frames.map((f) => Math.min(f.w, f.h))) * 0.3 : 0;
  return (
    <svg
      className="mindmap-pdf-preview"
      viewBox={`${vb.x - pad} ${vb.y - pad} ${vb.w + 2 * pad} ${vb.h + 2 * pad}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Vorschau der Blattaufteilung"
    >
      <rect x={vb.x - pad} y={vb.y - pad} width={vb.w + 2 * pad} height={vb.h + 2 * pad} fill="#fff" />
      {snap.nodes.map((n, i) => (
        <rect
          key={i}
          x={n.x}
          y={n.y}
          width={n.w}
          height={n.h}
          rx={Math.min(8, n.h / 2)}
          fill={`rgb(${(n.type === "q" && n.bar ? n.bar : n.fill).join(",")})`}
          opacity={n.type === "q" ? 0.45 : 0.9}
        />
      ))}
      {frames.map((f, i) => (
        <g key={i}>
          <rect
            x={f.x}
            y={f.y}
            width={f.w}
            height={f.h}
            fill="none"
            stroke="#2E6DA4"
            strokeWidth={stroke}
            strokeDasharray={mode === "tiles" ? `${stroke * 4} ${stroke * 2}` : undefined}
          />
          {f.label && frames.length <= 60 && (
            <text x={f.x + f.w * 0.05} y={f.y + fontSize * 1.1} fontSize={fontSize} fill="#2E6DA4" fontWeight="700">
              {f.label}
            </text>
          )}
        </g>
      ))}
    </svg>
  );
};

const ResearchMindmapPdfDialog = ({
  snapshot,
  title,
  onClose,
  levelStates,
  anyCollapsed,
  filtering,
  onLevelAction,
}) => {
  const [mode, setMode] = useState("single");
  const [singleFormat, setSingleFormat] = useState(() => defaultSingleFormat(snapshot));
  const [singleOrient, setSingleOrient] = useState("auto");
  const [tileFormat, setTileFormat] = useState("A3");
  const [tileOrient, setTileOrient] = useState("landscape");
  const [percent, setPercent] = useState(70);
  const [overlap, setOverlap] = useState(0);
  const [overview, setOverview] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const pct = Math.max(5, Math.min(400, Number(percent) || 0));
  const ov = Math.max(0, Math.min(30, Number(overlap) || 0));

  const fit = useMemo(() => {
    const f = fitSinglePage(snapshot, singleFormat, singleOrient);
    const [pw, ph] = PAPER_FORMATS[singleFormat];
    const [w, h] = f.orientation === "portrait" ? [pw, ph] : [ph, pw];
    return { ...f, area: [w - 2 * PAGE_MARGIN, h - 2 * PAGE_MARGIN] };
  }, [snapshot, singleFormat, singleOrient]);

  const singleTable = useMemo(
    () => FORMATS.map((f) => ({ format: f, ...fitSinglePage(snapshot, f, singleOrient) })),
    [snapshot, singleOrient]
  );

  const tiles = useMemo(
    () => computeTiles(snapshot, tileFormat, tileOrient, pct, ov),
    [snapshot, tileFormat, tileOrient, pct, ov]
  );

  const tileTable = useMemo(() => {
    const list = Array.from(new Set([...TILE_SCALE_PRESETS, Math.round(pct)])).sort((a, b) => a - b);
    return list.map((p) => ({
      percent: p,
      landscape: computeTiles(snapshot, tileFormat, "landscape", p, ov),
      portrait: computeTiles(snapshot, tileFormat, "portrait", p, ov),
    }));
  }, [snapshot, tileFormat, ov, pct]);

  const onePageMax = useMemo(
    () => maxPercentForSheets(snapshot, tileFormat, tileOrient, 1, ov),
    [snapshot, tileFormat, tileOrient, ov]
  );

  const tooMany = mode === "tiles" && tiles.count > MAX_TILES;
  const current = mode === "single" ? fit : tiles;
  const rd = readability(current.fontPt);

  const handleExport = async () => {
    setBusy(true);
    setError("");
    // Einen Tick warten, damit der Busy-Zustand gerendert wird, bevor jsPDF
    // den Main-Thread belegt.
    await new Promise((r) => window.setTimeout(r, 30));
    try {
      await exportMindmapPdf(snapshot, {
        mode,
        title,
        format: mode === "single" ? singleFormat : tileFormat,
        orientation: mode === "single" ? singleOrient : tileOrient,
        percent: pct,
        overlapMm: ov,
        overview,
        viewLabel: `${levelsLabel(levelStates, anyCollapsed)}${filtering ? " · Suchfilter aktiv" : ""}`,
      });
      onClose();
    } catch (e) {
      console.error("Mindmap-PDF-Export fehlgeschlagen:", e);
      setError(e?.message || "PDF konnte nicht erstellt werden.");
    } finally {
      setBusy(false);
    }
  };

  return (
    // Hintergrundklick schliesst bewusst nicht (ADR 0060): die Einstellungen
    // gingen sonst bei einem Fehlklick verloren.
    <div className="research-modal-overlay">
      <div className="research-modal mindmap-pdf-modal" role="dialog" aria-modal="true" aria-label="Mindmap als PDF exportieren">
        <div className="research-modal-header">
          <h3>Mindmap als PDF exportieren</h3>
          <button type="button" className="research-modal-close" onClick={onClose} aria-label="Schliessen">
            ×
          </button>
        </div>
        <div className="research-modal-body">
          <div className="mindmap-pdf-levels">
            <ResearchMindmapLevelChips
              states={levelStates}
              onToggle={(n) => onLevelAction(n)}
              onAll={() => onLevelAction("all")}
              onNone={() => onLevelAction("none")}
            />
          </div>
          <p className="mindmap-pdf-hint">
            Export: {levelsLabel(levelStates, anyCollapsed)}
            {filtering ? " · Suchfilter aktiv (Treffer mit Vorfahren)" : ""}.
            {" "}Abgewählte Ebenen werden ausgeblendet, ihre Einträge rücken an die nächsthöhere
            sichtbare Ebene.
            {anyCollapsed && (
              <>
                {" "}Einzelne Äste sind zugeklappt.{" "}
                <button type="button" className="mindmap-level-link" onClick={() => onLevelAction("expand")}>
                  Alle aufklappen
                </button>
              </>
            )}
          </p>

          <div className="mindmap-pdf-modes" role="radiogroup" aria-label="Aufteilung">
            <label className={`mindmap-pdf-mode${mode === "single" ? " is-active" : ""}`}>
              <input type="radio" name="mm-pdf-mode" checked={mode === "single"} onChange={() => setMode("single")} />
              <span>
                <strong>Auf ein Blatt</strong>
                <small>Ganze Mindmap auf ein Format skaliert</small>
              </span>
            </label>
            <label className={`mindmap-pdf-mode${mode === "tiles" ? " is-active" : ""}`}>
              <input type="radio" name="mm-pdf-mode" checked={mode === "tiles"} onChange={() => setMode("tiles")} />
              <span>
                <strong>Einzelblätter</strong>
                <small>In Massstab aufteilen, zum Zusammenkleben</small>
              </span>
            </label>
          </div>

          {mode === "single" ? (
            <>
              <div className="mindmap-pdf-fields">
                <label>
                  Format
                  <select value={singleFormat} onChange={(e) => setSingleFormat(e.target.value)}>
                    {FORMATS.map((f) => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Ausrichtung
                  <select value={singleOrient} onChange={(e) => setSingleOrient(e.target.value)}>
                    <option value="auto">Automatisch</option>
                    <option value="landscape">Quer</option>
                    <option value="portrait">Hoch</option>
                  </select>
                </label>
              </div>
              <table className="mindmap-pdf-table">
                <thead>
                  <tr>
                    <th>Format</th>
                    <th>Ausrichtung</th>
                    <th>Massstab</th>
                    <th>Schrift</th>
                  </tr>
                </thead>
                <tbody>
                  {singleTable.map((row) => {
                    const r = readability(row.fontPt);
                    return (
                      <tr
                        key={row.format}
                        className={row.format === singleFormat ? "is-selected" : ""}
                        onClick={() => setSingleFormat(row.format)}
                      >
                        <td>{row.format}</td>
                        <td>{ORIENT_LABEL[row.orientation]}</td>
                        <td>{Math.round(row.percent)} %</td>
                        <td>
                          <span className={`mindmap-pdf-read ${r.cls}`}>
                            {fmtPt(row.fontPt)} pt · {r.text}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          ) : (
            <>
              <div className="mindmap-pdf-fields">
                <label>
                  Format
                  <select value={tileFormat} onChange={(e) => setTileFormat(e.target.value)}>
                    {FORMATS.map((f) => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Ausrichtung
                  <select value={tileOrient} onChange={(e) => setTileOrient(e.target.value)}>
                    <option value="landscape">Quer</option>
                    <option value="portrait">Hoch</option>
                  </select>
                </label>
                <label>
                  Überlappung (mm)
                  <input type="number" min={0} max={30} value={overlap} onChange={(e) => setOverlap(e.target.value)} />
                </label>
              </div>
              <div className="mindmap-pdf-scale">
                <label htmlFor="mm-pdf-scale">Massstab</label>
                <input
                  id="mm-pdf-scale"
                  type="range"
                  min={10}
                  max={200}
                  step={5}
                  value={Math.min(200, Math.max(10, pct))}
                  onChange={(e) => setPercent(e.target.value)}
                />
                <input
                  type="number"
                  min={5}
                  max={400}
                  value={percent}
                  onChange={(e) => setPercent(e.target.value)}
                  aria-label="Massstab in Prozent"
                />
                <span>%</span>
              </div>
              <table className="mindmap-pdf-table">
                <thead>
                  <tr>
                    <th>Massstab</th>
                    <th>Schrift</th>
                    <th>Blätter {tileFormat} quer</th>
                    <th>Blätter {tileFormat} hoch</th>
                  </tr>
                </thead>
                <tbody>
                  {tileTable.map((row) => {
                    const r = readability(row.landscape.fontPt);
                    const cell = (o) => {
                      const t = row[o];
                      const sel = Math.round(pct) === row.percent && tileOrient === o;
                      return (
                        <td
                          className={`mindmap-pdf-cell${sel ? " is-selected" : ""}`}
                          onClick={() => {
                            setPercent(row.percent);
                            setTileOrient(o);
                          }}
                          title={`${row.percent} %, ${ORIENT_LABEL[o]} übernehmen`}
                        >
                          {t.count > MAX_TILES ? `> ${MAX_TILES}` : t.count === t.cols * t.rows ? `${t.count} (${t.cols} × ${t.rows})` : `${t.count} (Raster ${t.cols} × ${t.rows})`}
                        </td>
                      );
                    };
                    return (
                      <tr key={row.percent} className={Math.round(pct) === row.percent ? "is-current" : ""}>
                        <td>{row.percent} %</td>
                        <td>
                          <span className={`mindmap-pdf-read ${r.cls}`}>{fmtPt(row.landscape.fontPt)} pt</span>
                        </td>
                        {cell("landscape")}
                        {cell("portrait")}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {onePageMax != null && (
                <p className="mindmap-pdf-hint">
                  Auf ein einzelnes Blatt {tileFormat} {ORIENT_LABEL[tileOrient]} passt die Mindmap bis {onePageMax} %.
                </p>
              )}
              <p className="mindmap-pdf-hint">
                Blatt 1 beginnt oben links, die Blätter schliessen nebeneinander und untereinander
                direkt an. Die Schnitte werden in Lücken zwischen den Knoten gelegt, rechts und unten
                markiert eine gestrichelte Schnittkante das anschliessende Blatt. Leere Blätter entfallen.
              </p>
              <label className="mindmap-pdf-check">
                <input type="checkbox" checked={overview} onChange={(e) => setOverview(e.target.checked)} />
                Übersichtsblatt mit Blattplan voranstellen
              </label>
            </>
          )}

          <div className="mindmap-pdf-summary">
            {mode === "single" ? (
              <>
                1 Blatt {singleFormat} {ORIENT_LABEL[fit.orientation]} · Massstab {Math.round(fit.percent)} %
              </>
            ) : tooMany ? (
              <>Zu viele Blätter ({tiles.count}) — maximal {MAX_TILES}. Massstab verkleinern oder grösseres Format wählen.</>
            ) : (
              <>
                {tiles.count} Blatt{tiles.count === 1 ? "" : "er"} {tileFormat} {ORIENT_LABEL[tileOrient]} (Raster {tiles.cols} × {tiles.rows}
                {tiles.count < tiles.cols * tiles.rows ? `, ${tiles.cols * tiles.rows - tiles.count} leere entfallen` : ""})
                {overview && tiles.count > 1 ? " + Übersichtsblatt" : ""} · Massstab {pct} %
              </>
            )}
            {" · "}
            <span className={`mindmap-pdf-read ${rd.cls}`}>
              Schrift ca. {fmtPt(current.fontPt)} pt ({rd.text})
            </span>
          </div>

          <Preview snap={snapshot} mode={mode} fit={fit} tiles={tiles} />

          {error && <p className="mindmap-pdf-error">{error}</p>}

          <div className="mindmap-pdf-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
              Abbrechen
            </button>
            <button type="button" className="btn btn-primary" onClick={handleExport} disabled={busy || tooMany}>
              {busy ? "PDF wird erstellt …" : "PDF erstellen"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResearchMindmapPdfDialog;
